import { ScheduleFailureCategory } from "@/enum/schedule.enum";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list-person-schedule.schema";

/** The subset of a sending user the node tooltip needs. */
export interface NodeSender {
	id?: number;
	firstName?: string | null;
	lastName?: string | null;
}

/** What one campaign-flow node should say about this lead's progress through it. */
export interface NodeScheduleState {
	alreadySent: boolean;
	sentByUserName?: string;
	sentAt?: Date;
	sentWithEmail?: string;
	sentMessage?: string;
	sentSubject?: string;
	scheduledFor?: Date;
	bounced: boolean;
	failed: boolean;
	failureCategory: ScheduleFailureCategory | null;
}

/** Schedules that belong to this lead's campaign and were not called off. */
const relevantSchedules = (listPerson: ListPerson | undefined): ListPersonSchedule[] =>
	(listPerson?.schedules ?? []).filter(
		(schedule) =>
			!schedule.canceledAt &&
			!!listPerson?.campaignId &&
			schedule.campaignId === listPerson.campaignId
	);

/**
 * A step counts as executed once it was processed, failed, or bounced — i.e. the campaign moved
 * past it. Note this deliberately includes failures: `alreadySent` still gates whether the node can
 * be edited or deleted, and a failed step has been consumed just the same. Whether it *reads* as
 * sent is a separate question, answered by `failed` below.
 */
const wasExecuted = (schedule: ListPersonSchedule): boolean =>
	!!schedule.processedAt || !!schedule.failedAt || !!schedule.bouncedAt;

/**
 * Resolve what a single campaign-flow node should display for this lead.
 *
 * The starter node stands in for the campaign as a whole, so it reports "sent"/"scheduled" as soon
 * as *any* step has been, rather than matching on its own id.
 */
export const resolveNodeScheduleState = (
	listPerson: ListPerson | undefined,
	node: { id: string; type?: string },
	senders: NodeSender[] | undefined
): NodeScheduleState => {
	const schedules = relevantSchedules(listPerson);
	const isStarter = node.type === "starterNode";

	const executedHere = schedules.find(
		(schedule) => wasExecuted(schedule) && schedule.stepId === node.id
	);
	const executedAnywhere = schedules.find(wasExecuted);

	const scheduledHere = schedules.find(
		(schedule) => !!schedule.scheduledFor && schedule.stepId === node.id
	);
	const scheduledAnywhere = schedules.find((schedule) => !!schedule.scheduledFor);

	const failedHere = schedules.find(
		(schedule) => !!schedule.failedAt && schedule.stepId === node.id
	);
	const bouncedHere = schedules.find(
		(schedule) => !!schedule.bouncedAt && schedule.stepId === node.id
	);

	// The starter node falls back to the campaign-wide schedule; every other node matches its own.
	const sentSchedule = executedHere ?? (isStarter ? executedAnywhere : undefined);
	const scheduled = isStarter ? scheduledAnywhere : scheduledHere;

	const sentByUser = sentSchedule
		? senders?.find((sender) => sender.id === sentSchedule.userId)
		: undefined;
	const sentByUserName = sentByUser
		? `${sentByUser.firstName ?? ""} ${sentByUser.lastName ?? ""}`.trim()
		: undefined;

	return {
		alreadySent: !!(isStarter ? executedAnywhere : executedHere),
		sentByUserName,
		sentAt: sentSchedule?.processedAt
			? new Date(sentSchedule.processedAt)
			: sentSchedule?.canceledAt
				? new Date(sentSchedule.canceledAt)
				: undefined,
		sentWithEmail: sentSchedule?.email?.email,
		// Step-specific only (not the starterNode fallback): the content that actually went out,
		// captured on the schedule's conversation.
		sentMessage: executedHere?.conversation?.message,
		sentSubject: executedHere?.conversation?.subject ?? undefined,
		scheduledFor: scheduled?.scheduledFor ? new Date(scheduled.scheduledFor) : undefined,
		bounced: !!bouncedHere,
		failed: !!failedHere,
		failureCategory: failedHere
			? (failedHere.failureCategory ?? ScheduleFailureCategory.UNKNOWN)
			: null,
	};
};
